import { useMemo } from "react";
import { OperationVariables } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {
  storageFormatters,
  type GeneralBarDatum,
  type TotalsAnnotationsValues,
} from "@jana-technology/platform-ui";

import { TotalSystemOverviewChartsDocument } from "$/__generated__/graphql";

import {
  appendORIRData,
  appendSystemOverviewTooltipExtras,
  appendSystemOverviewSubgroups,
  makeBaseORIRDatum,
  makeORTabData,
} from "./utils";

export const useTotalSystemOverviewCharts = (options?: OperationVariables) => {
  const result = useQuery(TotalSystemOverviewChartsDocument, options);

  const series = useMemo(() => {
    const threatORTab: Array<GeneralBarDatum> = [];
    const threatIRTab: Array<GeneralBarDatum> = [];
    const consequenceORTab: Array<GeneralBarDatum> = [];
    const consequenceIRTab: Array<GeneralBarDatum> = [];
    const outcomeORTab: Array<GeneralBarDatum> = [];
    const outcomeIRTab: Array<GeneralBarDatum> = [];

    let threatORDataKeys: Array<string> = [];
    const threatIRDataKeys: Array<string> = [];
    const consequenceORDataKeys: Array<string> = [];
    const consequenceIRDataKeys: Array<string> = [];
    const outcomeORDataKeys: Array<string> = [];
    const outcomeIRDataKeys: Array<string> = [];

    const totals: TotalsAnnotationsValues = [];

    let yMaxStackVal;

    if (result.data) {
      const { totalSystemOverviewCharts } = result.data;

      for (let i = 0; i < totalSystemOverviewCharts.length; i++) {
        const chart = totalSystemOverviewCharts[i];
        const xBand = chart.fieldName ?? `Field${i + 1}`;

        const [threatORDatum, threatIRDatum] = makeBaseORIRDatum(xBand);

        const [consequenceORDatum, consequenceIRDatum] =
          makeBaseORIRDatum(xBand);

        const [outcomeORDatum, outcomeIRDatum] = makeBaseORIRDatum(xBand);

        threatORDatum.formatter = (value: number) =>
          storageFormatters.currency(value);
        threatIRDatum.formatter = (value: number) =>
          storageFormatters.currency(value);
        consequenceORDatum.formatter = (value: number) =>
          storageFormatters.currency(value);
        consequenceIRDatum.formatter = (value: number) =>
          storageFormatters.currency(value);
        outcomeORDatum.formatter = (value: number) =>
          storageFormatters.currency(value);
        outcomeIRDatum.formatter = (value: number) =>
          storageFormatters.currency(value);

        appendORIRData(
          chart.itemsByThreat,
          threatORDatum,
          threatORDataKeys,
          threatIRDatum,
          threatIRDataKeys,
        );

        threatORDataKeys = threatORDataKeys.filter(
          (key) => !key.includes("Intervention"),
        );

        appendORIRData(
          chart.itemsByConsequence,
          consequenceORDatum,
          consequenceORDataKeys,
          consequenceIRDatum,
          consequenceIRDataKeys,
        );

        appendORIRData(
          chart.itemsByOutcomeScenario,
          outcomeORDatum,
          outcomeORDataKeys,
          outcomeIRDatum,
          outcomeIRDataKeys,
        );

        appendSystemOverviewSubgroups(
          chart.itemsByConsequence,
          consequenceORDatum,
          consequenceIRDatum,
          "By Category",
        );

        appendSystemOverviewSubgroups(
          chart.itemsByOutcomeScenario,
          outcomeORDatum,
          outcomeIRDatum,
          "Release Outcome",
        );

        appendSystemOverviewTooltipExtras(
          chart.operationalTotal ?? 0,
          chart.interventionTotal ?? 0,
          threatORDatum,
          threatIRDatum,
        );

        appendSystemOverviewTooltipExtras(
          chart.operationalTotal ?? 0,
          chart.interventionTotal ?? 0,
          consequenceORDatum,
          consequenceIRDatum,
        );

        appendSystemOverviewTooltipExtras(
          chart.operationalTotal ?? 0,
          chart.interventionTotal ?? 0,
          outcomeORDatum,
          outcomeIRDatum,
        );

        threatIRTab.push(threatORDatum, threatIRDatum);

        consequenceIRTab.push(consequenceORDatum, consequenceIRDatum);

        outcomeIRTab.push(outcomeORDatum, outcomeIRDatum);

        totals.push({
          band: xBand,
          total: chart.total ?? 0,
        });
      }

      // OR charts are not grouped; filter out IR series
      makeORTabData(threatIRTab, threatORTab);
      makeORTabData(consequenceIRTab, consequenceORTab);
      makeORTabData(outcomeIRTab, outcomeORTab);

      yMaxStackVal = Math.max(...totals.map((annotation) => annotation.total));
    }

    return {
      threatORTab,
      threatORDataKeys,
      threatIRTab,
      threatIRDataKeys,
      consequenceORTab,
      consequenceORDataKeys,
      consequenceIRTab,
      consequenceIRDataKeys,
      outcomeORTab,
      outcomeORDataKeys,
      outcomeIRTab,
      outcomeIRDataKeys,
      totals,
      yMaxStackVal,
    };
  }, [result.data]);

  return {
    ...result,
    ...series,
  };
};
